"use strict";
// Plugin registry — validates manifests and resolves which plugins are active
// in a project, in what order, and what is missing.
//
// Static validation (at construction) catches contract errors at build time:
// unknown points, major mismatches, undeclared feature flags, id/name rules.
//
// Resolution (per project) answers, for a set of enabled plugin ids:
//
//   enabled ──▶ + builtins ──▶ edges ──▶ topological order
//                                │
//      requires P ──▶ depends on every enabled provider of P
//                     (unless it declares P itself: then it's a presence check)
//      provides P ──▶ depends on P's declarer (declarer mounts first)
//
//   missing   required point with no enabled provider (or none with the
//             required features)            → resolution not ok
//   degraded  optional point with no provider → plugin works, reduced
//   inert     provides a point whose declarer isn't enabled → does nothing
//   conflicts cardinality-one point with 2+ enabled providers → not ok
//   cycles    dependency cycle               → not ok
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluginRegistry = void 0;
exports.provisionsOf = provisionsOf;
exports.createPluginRegistry = createPluginRegistry;
const core_points_1 = require("./core-points");
const ID_RE = /^[a-z][a-z0-9-]*$/;
const POINT_RE = /^[a-z][a-z0-9-]*(\.[a-z][a-zA-Z0-9-]*)+$/;
const SEMVER_RE = /^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/;
function provisionsOf(manifest, point) {
    const raw = manifest.provides?.[point];
    if (!raw) {
        return [];
    }
    return Array.isArray(raw) ? [...raw] : [raw];
}
function hasFeature(provision, feature) {
    const value = provision.features?.[feature];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
}
class PluginRegistry {
    constructor(manifests, options = {}) {
        this._plugins = new Map();
        this._declarers = new Map();
        this.issues = [];
        const all = options.includeBuiltins === false ? manifests : [...core_points_1.BUILTIN_PLUGINS, ...manifests];
        for (const m of all) {
            if (this._plugins.has(m.id)) {
                this._issue('error', m.id, 'duplicate-id', `Plugin id "${m.id}" is registered twice.`);
                continue;
            }
            this._plugins.set(m.id, m);
        }
        for (const m of this._plugins.values()) {
            this._validateShape(m);
            for (const [point, decl] of Object.entries(m.declares ?? {})) {
                const existing = this._declarers.get(point);
                if (existing) {
                    this._issue('error', m.id, 'duplicate-point', `"${point}" is already declared by "${existing.plugin.id}".`);
                    continue;
                }
                this._declarers.set(point, { plugin: m, decl });
            }
        }
        for (const m of this._plugins.values()) {
            this._validateReferences(m);
        }
    }
    get errors() {
        return this.issues.filter((i) => i.level === 'error');
    }
    get(id) {
        return this._plugins.get(id);
    }
    all() {
        return [...this._plugins.values()];
    }
    declaration(point) {
        return this._declarers.get(point)?.decl;
    }
    declarerOf(point) {
        return this._declarers.get(point)?.plugin;
    }
    /** Providers of a point, optionally limited to an active set. */
    providersOf(point, active) {
        const activeSet = active ? new Set(active) : undefined;
        const out = [];
        for (const plugin of this._plugins.values()) {
            if (activeSet && !activeSet.has(plugin.id)) {
                continue;
            }
            for (const provision of provisionsOf(plugin, point)) {
                out.push({ plugin, provision });
            }
        }
        return out;
    }
    /** Resolve a project's enabled plugins. Builtins are always active. */
    resolve(enabledIds) {
        const unknown = enabledIds.filter((id) => !this._plugins.has(id));
        const active = new Set([...core_points_1.BUILTIN_PLUGINS.map((b) => b.id).filter((id) => this._plugins.has(id))]);
        for (const id of enabledIds) {
            if (this._plugins.has(id)) {
                active.add(id);
            }
        }
        const missing = [];
        const degraded = [];
        const inert = [];
        const conflicts = [];
        const deps = new Map([...active].map((id) => [id, new Set()]));
        for (const id of active) {
            const m = this._plugins.get(id);
            for (const [point, req] of Object.entries(m.requires ?? {})) {
                const providers = this.providersOf(point, active).filter((p) => p.plugin.id !== id);
                const capable = providers.filter((p) => (req.features ?? []).every((f) => hasFeature(p.provision, f)));
                if (capable.length === 0) {
                    if (req.optional) {
                        degraded.push({ plugin: id, point });
                    }
                    else {
                        missing.push({
                            plugin: id,
                            point,
                            requirement: req,
                            reason: providers.length ? 'features' : 'no-provider',
                        });
                    }
                }
                // Requiring a point you declare yourself (payments needs ≥1 provider)
                // is a presence check, not an ordering edge: providers mount after
                // their declarer, so an edge here would always be a cycle.
                if (this._declarers.get(point)?.plugin.id === id) {
                    continue;
                }
                for (const p of providers) {
                    deps.get(id).add(p.plugin.id);
                }
            }
            for (const point of Object.keys(m.provides ?? {})) {
                const declarer = this._declarers.get(point)?.plugin.id;
                if (!declarer || declarer === id) {
                    continue;
                }
                if (!active.has(declarer)) {
                    inert.push({ plugin: id, point });
                }
                else {
                    deps.get(id).add(declarer);
                }
            }
        }
        for (const [point, { decl }] of this._declarers) {
            if (decl.cardinality !== 'one') {
                continue;
            }
            const providers = [...new Set(this.providersOf(point, active).map((p) => p.plugin.id))];
            if (providers.length > 1) {
                conflicts.push({ point, providers });
            }
        }
        const { order, cycles } = topoSort(deps);
        return {
            ok: missing.length === 0 && conflicts.length === 0 && cycles.length === 0 && unknown.length === 0,
            order,
            missing,
            degraded,
            inert,
            conflicts,
            cycles,
            unknown,
        };
    }
    _issue(level, plugin, code, message) {
        this.issues.push({ level, plugin, code, message });
    }
    _validateShape(m) {
        if (!ID_RE.test(m.id)) {
            this._issue('error', m.id, 'bad-id', `Plugin id "${m.id}" must be lowercase kebab-case.`);
        }
        if (!SEMVER_RE.test(m.version)) {
            this._issue('error', m.id, 'bad-version', `Version "${m.version}" is not semver (x.y.z).`);
        }
        for (const [point, decl] of Object.entries(m.declares ?? {})) {
            if (!POINT_RE.test(point)) {
                this._issue('error', m.id, 'bad-point-name', `Point "${point}" must be "<owner>.<name>".`);
            }
            else if (!point.startsWith(`${m.id}.`)) {
                this._issue('error', m.id, 'point-owner', `"${m.id}" may only declare points under "${m.id}.*", not "${point}".`);
            }
            if (!Number.isInteger(decl.major) || decl.major < 1) {
                this._issue('error', m.id, 'bad-major', `Point "${point}" needs an integer major >= 1.`);
            }
        }
        for (const event of Object.keys(m.emits ?? {})) {
            if (!event.startsWith(`${m.id}.`)) {
                this._issue('error', m.id, 'event-owner', `"${m.id}" may only emit events under "${m.id}.*", not "${event}".`);
            }
        }
        if (m.trust === 'third-party' && m.provides?.['core.studio.module']) {
            this._issue('error', m.id, 'third-party-host', 'Third-party plugins cannot ship studio modules.');
        }
    }
    _validateReferences(m) {
        for (const point of Object.keys(m.provides ?? {})) {
            const declared = this._declarers.get(point);
            if (!declared) {
                this._issue('error', m.id, 'unknown-point', `Provides "${point}", which no plugin declares.`);
                continue;
            }
            for (const provision of provisionsOf(m, point)) {
                if (provision.major !== declared.decl.major) {
                    this._issue('error', m.id, 'major-mismatch', `Provides "${point}@${provision.major}" but "${declared.plugin.id}" declares @${declared.decl.major}.`);
                }
                this._checkFeatures(m.id, point, Object.keys(provision.features ?? {}), declared.decl);
            }
        }
        for (const [point, req] of Object.entries(m.requires ?? {})) {
            const declared = this._declarers.get(point);
            if (!declared) {
                this._issue(req.optional ? 'warning' : 'error', m.id, 'unknown-point', `Requires "${point}", which no plugin declares.`);
                continue;
            }
            if (req.major !== declared.decl.major) {
                this._issue('error', m.id, 'major-mismatch', `Requires "${point}@${req.major}" but it is @${declared.decl.major}.`);
            }
            this._checkFeatures(m.id, point, req.features ?? [], declared.decl);
        }
        for (const event of m.subscribes ?? []) {
            const emitted = [...this._plugins.values()].some((p) => p.emits && event in p.emits);
            if (!emitted) {
                this._issue('warning', m.id, 'unknown-event', `Subscribes to "${event}", which no plugin emits.`);
            }
        }
    }
    _checkFeatures(pluginId, point, features, decl) {
        const allowed = new Set(decl.features ?? []);
        for (const f of features) {
            if (!allowed.has(f)) {
                this._issue('error', pluginId, 'unknown-feature', `"${point}" has no feature "${f}".`);
            }
        }
    }
}
exports.PluginRegistry = PluginRegistry;
/** Kahn's algorithm, deterministic (alphabetical among ready nodes). */
function topoSort(deps) {
    const remaining = new Map([...deps].map(([id, d]) => [id, new Set([...d].filter((x) => deps.has(x)))]));
    const order = [];
    while (remaining.size) {
        const ready = [...remaining]
            .filter(([, d]) => d.size === 0)
            .map(([id]) => id)
            .sort();
        if (ready.length === 0) {
            break;
        }
        for (const id of ready) {
            order.push(id);
            remaining.delete(id);
            for (const d of remaining.values()) {
                d.delete(id);
            }
        }
    }
    const cycles = remaining.size ? [[...remaining.keys()].sort()] : [];
    return { order, cycles };
}
/** Build a registry and throw on contract errors. Use at module load for the first-party catalog. */
function createPluginRegistry(manifests) {
    const registry = new PluginRegistry(manifests);
    if (registry.errors.length) {
        const lines = registry.errors.map((e) => `  [${e.plugin}] ${e.code}: ${e.message}`).join('\n');
        throw new Error(`Invalid plugin catalog:\n${lines}`);
    }
    return registry;
}
